import { useCallback, useEffect } from 'react';
import { ConfirmPopup, Text } from '~/commonComponents';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import { CandidateStatus, useUpdateCandidateStatus } from '~/domain/PermanentJobs';
import { IToggleControls } from '~/hooks/useToggle';
import WarningIcon from '~/images/icons/Warning';
import { Colors } from '~/themes/colors';

interface IProps {
  controls: IToggleControls;
  candidateId: string;
  professionalName: string;
}

const SuccessIconComponent = () => <AnimatedCheckCircle />;
const WarningIconComponent = () => (
  <WarningIcon width={80} height={80} color={Colors.warning_500} />
);

export const RejectPopupConfirmation = ({ controls, candidateId, professionalName }: IProps) => {
  const { isError, isPending, isSuccess, mutate, reset } = useUpdateCandidateStatus();

  const handleConfirmRejectCandidate = () =>
    mutate({ candidateIds: [candidateId], status: CandidateStatus.Rejected });

  const handleClosePopup = useCallback(() => {
    reset();
    controls.setOff();
  }, [controls, reset]);

  useEffect(() => {
    if (isError) {
      handleClosePopup();
    }
  }, [handleClosePopup, isError]);

  return (
    <>
      {!isSuccess ? (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={WarningIconComponent}
          title="Before You Continue"
          titleStyle={{ marginTop: 21, marginBottom: 20 }}
          description={
            <Text color={Colors.neutral_500}>
              This action is permanent. Once rejected,{' '}
              <Text bold color={Colors.neutral_500}>
                {professionalName}
              </Text>{' '}
              will no
              <br />
              longer be able to apply to onsite interviews for this job.
              <br />
              Do you want to continue?
            </Text>
          }
          descriptionStyle={{ width: '100%', marginBottom: 38 }}
          modalStyle={{ width: 560 }}
          leftButtonAction={handleClosePopup}
          leftButtonDisabled={isPending}
          leftButtonText="Go Back"
          rightButtonAction={handleConfirmRejectCandidate}
          rightButtonLoading={isPending}
          rightButtonText="Yes, Reject"
        />
      ) : (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={SuccessIconComponent}
          title="Candidate Status Successfully Updated"
          titleStyle={{ marginTop: 21, marginBottom: 16 }}
          description={
            <Text color={Colors.neutral_500}>
              <Text bold color={Colors.neutral_500}>
                {professionalName}
              </Text>
              ’s application is no longer active and has been
              <br />
              archived under Rejected. Keep reviewing other
              <br />
              applicants to find your next great hire.
            </Text>
          }
          descriptionStyle={{ width: '100%', marginBottom: 38 }}
          modalStyle={{ width: 560 }}
          rightButtonText="Done"
          rightButtonAction={handleClosePopup}
        />
      )}
    </>
  );
};
